import Head from "next/head";
import { Tabs } from "@shared/ui/Tabs";
import { ProductLayout } from "@entities/Product/ProductLayout";
import React, { useMemo, useState } from "react";
import { ProductBenefits } from "@entities/Product/ProductBenefits";
import styled from "styled-components";
import ProductCalculator from "@entities/Product/ProductCalculator";
import ProductFaq from "@entities/Product/ProductFaq";
import { AnimatePresence } from "framer-motion";
import { Layout } from "@features/Layout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { axiosInstance } from "@shared/api";
import { IProduct } from "@entities/Product/types";
import { useRouter } from "next/router";
import { setCurrentProductId } from "@shared/model";

interface IProps {
  categories: IProduct[];
}

export default function ProductCategory(props: IProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const [, productSlug] = router.query.slug;
  const [activeTab, setActiveTab] = useState(0);

  const product = useMemo(() => {
    const currentProduct = props.categories.find(
      (item) => item.slug === productSlug
    );
    setCurrentProductId(currentProduct.id);

    return currentProduct;
  }, [productSlug, router.locale, props.categories]);

  const tabs = useMemo(
    () => [
      {
        id: 0,
        title: t(`Преимущества`),
        component: <ProductBenefits key={0} benefits={product.benefits} />,
      },
      {
        id: 1,
        title: t(`Калькулятор`),
        component: (
          <ProductCalculator
            key={1}
            initSum={1_000_000}
            minSum={500_000}
            maxSum={58_000_000}
            stepSum={50_000}
            fromSum={t(`от {{fromSum}} тыс.`, { fromSum: 500 })}
            toSum={t(`до {{toSum}} млн.`, { toSum: 58 })}
            initTerm={24}
            minTerm={1}
            maxTerm={84}
            stepTerm={1}
            fromTerm={t(`от {{fromTerm}} мес.`, { fromTerm: 1 })}
            toTerm={t(`до {{toTerm}} лет`, { toTerm: 7 })}
            href={product.banner.url}
            rate={product.loan_rate}
            productId={product.id}
          />
        ),
      },
      {
        id: 2,
        title: t(`Вопросы и ответы`),
        component: <ProductFaq key={2} faqs={product.faqs} />,
      },
    ],
    [product, router.locale]
  );

  return (
    <>
      <Head>
        <title>{t(`БНК Финанс Казахстан`)}</title>
      </Head>
      <>
        <StyledDesktopContent>
          <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
          <StyledTabContent>
            <AnimatePresence mode="wait">
              {tabs[activeTab].component}
            </AnimatePresence>
          </StyledTabContent>
        </StyledDesktopContent>
        <StyledMobileContent>
          {tabs.map((tab) => tab.component)}
        </StyledMobileContent>
      </>
    </>
  );
}

ProductCategory.getLayout = function getLayout(page) {
  return (
    <Layout additionalButtonsVisible>
      <ProductLayout products={page.props.categories}>{page}</ProductLayout>
    </Layout>
  );
};

export async function getServerSideProps({ locale, ...rest }) {
  const [category, product] = rest.params.slug;

  try {
    const categoriesResponse = await axiosInstance.get(
      `/categories/${category}/products`,
      { headers: { "Accept-Language": locale } }
    );

    const routeLocale = locale === "ru" ? `` : `/${locale}`;

    const redirect = product
      ? {}
      : {
          redirect: {
            permanent: false,
            destination: `${routeLocale}/main/products/${category}/${categoriesResponse.data[0].slug}`,
          },
        };

    return {
      props: {
        ...(await serverSideTranslations(locale, ["common"])),
        categories: categoriesResponse.data,
      },
      ...redirect,
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
}

const StyledTabContent = styled.div`
  padding-top: 6.4rem;
  padding-bottom: 8.4rem;
`;

const StyledDesktopContent = styled.div`
  @media screen and (max-width: ${(props) => props.theme.sizes.tablet}) {
    display: none;
  }
`;

const StyledMobileContent = styled.div`
  display: none;
  @media screen and (max-width: ${(props) => props.theme.sizes.tablet}) {
    display: grid;
    grid-gap: 6.4rem;
    padding-bottom: 6.4rem;
  }
`;
